import React, { useState } from 'react';
import KitModal from '../UI/KitModal';
import { Warning } from '@phosphor-icons/react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { CompanySpacesService } from '../../service/CompanySpacesService';

interface Props {
  onClose: () => void;
  spaceId: string;
  onSuccess?: (newApiKey: string) => void;
}

const RefreshSpaceApiKeyModal: React.FC<Props> = ({
  onClose,
  spaceId,
  onSuccess,
}) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const response = await CompanySpacesService.refreshApiKey(spaceId);
      const newKey = response?.data?.apiKey;
      setNotification({
        type: NotificationType.Success,
        message: 'Space API key refreshed - copy your new key now',
      });
      if (newKey) onSuccess?.(newKey);
      onClose();
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error as string),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <KitModal show={true} onHide={onClose}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          padding: '40px 60px',
          paddingBottom: '20px',
        }}
      >
        <Warning size={80} color='rgba(240, 84, 80, 1)' />
        <Typography
          sx={{
            color: 'rgba(71, 84, 103, 1)',
            fontWeight: 500,
            marginTop: '10px',
          }}
        >
          Refresh Space API Key
        </Typography>
        <Typography
          sx={{
            color: 'rgba(71, 84, 103, 1)',
            lineHeight: '28px',
            width: '80%',
            letterSpacing: '0.15px',
            margin: '20px 0',
            textAlign: 'center',
          }}
        >
          This will immediately invalidate the current Space API key. Any
          integration still using the old key will stop working. This action
          cannot be undone.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Button variant='outlined' onClick={onClose} disabled={loading}>
            Go Back
          </Button>
          <Button
            variant='outlined'
            sx={{
              border: '1px solid rgba(240, 84, 80, 1)',
              color: 'rgba(240, 84, 80, 1)',
              background: 'transparent',
              '&:hover': {
                border: '1px solid rgba(240, 84, 80, 1)',
                color: 'rgba(240, 84, 80, 1)',
                background: 'transparent',
              },
            }}
            onClick={refresh}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Refresh Key'}
          </Button>
        </Box>
      </Box>
    </KitModal>
  );
};

export default RefreshSpaceApiKeyModal;
